/**
 * flushQueue — client-side replay path.
 *
 * The SW's own `sync` event handler also drains the queue, but it
 * cannot mint a fresh Supabase JWT (it has no auth session). This
 * client-side flush attaches the live access_token at replay time and
 * is invoked when the browser comes back online while a tab is open.
 *
 * Both paths are idempotent because every queued row carries an
 * Idempotency-Key, so a duplicate replay short-circuits to the cached
 * server response.
 */

import { MutationQueue }   from './queue'
import { createIdbStore }  from './idbStore'
import { supabase }        from '@/lib/supabase'

let _queue: MutationQueue | null = null

export function getClientQueue(): MutationQueue {
  if (_queue) return _queue
  _queue = new MutationQueue(createIdbStore())
  return _queue
}

export async function flushQueue(): Promise<{ replayed: number; failed: number }> {
  const queue = getClientQueue()
  const result = await queue.replay({
    fetch,
    freshAuthToken: async (): Promise<string | null> => {
      const { data } = await supabase.auth.getSession()
      return data.session?.access_token ?? null
    },
  })
  return { replayed: result.replayed, failed: result.failed }
}

/**
 * Posts a FLUSH_QUEUE message to the active service worker so it
 * runs its own replay. Used as a backup signal alongside the
 * Background-Sync registration; some browsers (Safari) don't ship
 * Background Sync, so the message channel is the universal trigger.
 */
export async function notifyServiceWorkerToFlush(): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return
  const reg = await navigator.serviceWorker.ready
  reg.active?.postMessage({ type: 'FLUSH_QUEUE' })
}
